import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { getBackendUrl, LoadStateInfo, loaded, loading, notStarted, error, failedResponse, INVALID_ID } from "../common";
import { SurveyInfo, SurveyStatus, SurveyType } from "./surveySlice";
import { getToken } from "../../features/user/UserAvatar";

export interface SurveyInsUptState {
    state: LoadStateInfo;
    survey: SurveyInfo;
}

const initialState: SurveyInsUptState = {
    state: notStarted(),
    survey: emptySurvey(),
};

function emptySurvey(): SurveyInfo {
    return {
        id: INVALID_ID,
        schoolId: "",
        type: SurveyType.PRIVATE_PER_USER,
        status: SurveyStatus.NOT_STARTED,
        target: 0,
        questions: [],
    };
}

export interface SurveyInsertRequest {
    survey: SurveyInfo;
}

export const surveyInsert = createAsyncThunk(
    "surveyInsert",
    async (param: SurveyInsertRequest, thunkAPI) => {
        console.debug("insert survey");
        const response = await fetch(`${getBackendUrl()}/api/survey`, {
            method: "POST",
            signal: thunkAPI.signal,
            headers: {
                "Authorization": `Bearer ${getToken()}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(param.survey)
        });
        if (response.ok) {
            return await response.json();
        } else {
            return thunkAPI.rejectWithValue(failedResponse(response));
        }
    }
);

export interface SurveyUpdateRequest {
    surveyId: string;
    survey: SurveyInfo;
}

export const surveyUpdate = createAsyncThunk(
    "surveyUpdate",
    async (param: SurveyUpdateRequest, thunkAPI) => {
        console.debug("update survey");
        const response = await fetch(`${getBackendUrl()}/api/survey/${param.surveyId}`, {
            method: "PUT",
            signal: thunkAPI.signal,
            headers: {
                "Authorization": `Bearer ${getToken()}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(param.survey)
        });
        if (response.ok) {
            return await response.json();
        } else {
            return thunkAPI.rejectWithValue(failedResponse(response));
        }
    }
);

export interface SurveyDeleteRequest {
    surveyId: string;
}

export const surveyDelete = createAsyncThunk(
    "surveyDelete",
    async (param: SurveyDeleteRequest, thunkAPI) => {
        console.debug("delete survey");
        const response = await fetch(`${getBackendUrl()}/api/survey/${param.surveyId}`, {
            method: "DELETE",
            signal: thunkAPI.signal,
            headers: {
                "Authorization": `Bearer ${getToken()}`,
            }
        });
        if (response.ok) {
            return param.surveyId;
        } else {
            return thunkAPI.rejectWithValue(failedResponse(response));
        }
    }
);

export const surveyInsUpdSlice = createSlice({
    name: "surveyInsUpd",
    initialState,
    reducers: {
        reset: (state) => {
            state.state = notStarted();
            state.survey = emptySurvey();
        },
        setSurvey: (state, action: PayloadAction<SurveyInfo>) => {
            state.survey = action.payload;
        },
        updateField: (state, action: PayloadAction<{ field: keyof SurveyInfo; value: any }>) => {
            (state.survey as any)[action.payload.field] = action.payload.value;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(surveyInsert.pending, (state) => {
                state.state = loading();
            })
            .addCase(surveyInsert.fulfilled, (state, action) => {
                state.state = loaded();
                state.survey = action.payload;
            })
            .addCase(surveyInsert.rejected, (state, action) => {
                state.state = error(action);
            })
            .addCase(surveyUpdate.pending, (state) => {
                state.state = loading();
            })
            .addCase(surveyUpdate.fulfilled, (state, action) => {
                state.state = loaded();
                state.survey = action.payload;
            })
            .addCase(surveyUpdate.rejected, (state, action) => {
                state.state = error(action);
            })
            .addCase(surveyDelete.pending, (state) => {
                state.state = loading();
            })
            .addCase(surveyDelete.fulfilled, (state) => {
                state.state = loaded();
                state.survey = emptySurvey();
            })
            .addCase(surveyDelete.rejected, (state, action) => {
                state.state = error(action);
            });
    },
});

export const { reset, setSurvey, updateField } = surveyInsUpdSlice.actions;

export const selectSurveyInsUpd = (state: RootState) => state.surveyInsUpd;

export default surveyInsUpdSlice.reducer;
